/**
 * The verified badge (Console v2 §3.9, slice S7): staff verified this
 * username, a real person or brand (public flag `UserFlags.NX_VERIFIED`).
 * Discord's shape: a check in a rounded rosette, in the accent colour,
 * beside the name. Nothing shows for an account without the flag.
 */
import { UserFlags, type User } from '@neosocial/protocol';
import { useT } from '../i18n';

/** True when staff verified this account's username. */
export function isVerified(user: Pick<User, 'public_flags'> | null | undefined): boolean {
  return ((user?.public_flags ?? 0) & UserFlags.NX_VERIFIED) !== 0;
}

export function VerifiedBadge({
  user,
  size = 18,
}: {
  user: Pick<User, 'public_flags'>;
  size?: number;
}) {
  const { t } = useT();
  if (!isVerified(user)) return null;
  const label = t('vb.verified');
  return (
    <span
      className="ns-vbadge"
      role="img"
      aria-label={label}
      title={label}
      data-verified
      style={{
        display: 'inline-flex',
        verticalAlign: '-0.15em',
        marginInlineStart: 6,
        color: 'var(--accent)',
        flex: 'none',
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        {/* an eight-lobed rosette: smooth, no sharp corners */}
        <path
          fill="currentColor"
          d="M12 1.6c1.1 0 2 .7 2.6 1.6.9-.4 2-.4 2.9.2.9.5 1.4 1.5 1.5 2.5 1 .1 2 .6 2.5 1.5.5.9.6 2 .2 2.9.9.6 1.6 1.5 1.6 2.6s-.7 2-1.6 2.6c.4.9.4 2-.2 2.9-.5.9-1.5 1.4-2.5 1.5-.1 1-.6 2-1.5 2.5-.9.5-2 .6-2.9.2-.6.9-1.5 1.6-2.6 1.6s-2-.7-2.6-1.6c-.9.4-2 .4-2.9-.2-.9-.5-1.4-1.5-1.5-2.5-1-.1-2-.6-2.5-1.5-.5-.9-.6-2-.2-2.9C2.3 14 1.6 13.1 1.6 12s.7-2 1.6-2.6c-.4-.9-.4-2 .2-2.9.5-.9 1.5-1.4 2.5-1.5.1-1 .6-2 1.5-2.5.9-.5 2-.6 2.9-.2.6-.9 1.5-1.6 2.6-1.6Z"
        />
        <path
          d="m8.2 12.3 2.6 2.6 5-5.2"
          fill="none"
          stroke="var(--text-on-accent, #fff)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
